import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';

export interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  contractType: 'employment' | 'mandate' | 'b2b';
  baseSalary: number;
  hourlyRate: number;
  startDate: string;
  createdAt?: string;
}

export interface PayrollRecord {
  id: string;
  employeeId: string;
  employeeName: string;
  month: string;
  totalHours: number;
  regularHours: number;
  overtime?: {
    hours50: number;
    hours100: number;
  };
  grossAmount: number;
  netAmount: number;
  deductions: {
    pension: number;
    disability: number;
    sickness: number;
    health: number;
    tax: number;
  };
  createdAt: string;
}

// Funkcja do wykrycia struktury tabeli
const detectTableStructure = async () => {
  try {
    const { data, error } = await supabase
      .from('employees')
      .select('*')
      .limit(1);
    
    if (error) {
      console.error('Error detecting table structure:', error);
      return null;
    }
    
    // Sprawdź które kolumny istnieją
    if (data && data.length > 0) {
      const sample = data[0];
      return {
        hasEmail: 'email' in sample,
        hasFirstName: 'first_name' in sample,
        hasName: 'name' in sample,
        hasFullName: 'full_name' in sample,
        // Dodaj więcej sprawdzeń według potrzeby
      };
    }
    
    return null;
  } catch (err) {
    console.error('Error in detectTableStructure:', err);
    return null;
  }
};

// Elastyczne mapowanie danych
const mapEmployeeFromDb = (dbEmployee: any, structure?: any): Employee => {
  // Próbuj różnych kombinacji pól
  let firstName = '';
  let lastName = '';
  let email = '';
  
  // Obsługa imienia i nazwiska
  if (dbEmployee.first_name) {
    firstName = dbEmployee.first_name;
    lastName = dbEmployee.last_name || '';
  } else if (dbEmployee.name) {
    const parts = dbEmployee.name.split(' ');
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  } else if (dbEmployee.full_name) {
    const parts = dbEmployee.full_name.split(' ');
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }
  
  // Obsługa emaila
  email = dbEmployee.email || dbEmployee.email_address || '';
  
  return {
    id: dbEmployee.id,
    firstName,
    lastName,
    email,
    phone: dbEmployee.phone || dbEmployee.phone_number || '',
    contractType: dbEmployee.contract_type || 'employment',
    baseSalary: dbEmployee.base_salary || dbEmployee.salary || 0,
    hourlyRate: dbEmployee.hourly_rate || 0,
    startDate: dbEmployee.start_date || dbEmployee.hire_date || new Date().toISOString(),
    createdAt: dbEmployee.created_at
  };
};

const mapEmployeeToDb = (employee: any, structure?: any) => {
  const dbData: any = {
    contract_type: employee.contractType,
    base_salary: employee.baseSalary,
    hourly_rate: employee.hourlyRate,
    start_date: employee.startDate,
    is_active: true
  };
  
  // Dodaj pola w zależności od struktury tabeli
  if (!structure || structure.hasFirstName) {
    dbData.first_name = employee.firstName;
    dbData.last_name = employee.lastName;
  } else if (structure?.hasName) {
    dbData.name = `${employee.firstName} ${employee.lastName}`.trim();
  } else if (structure?.hasFullName) {
    dbData.full_name = `${employee.firstName} ${employee.lastName}`.trim();
  }
  
  if (!structure || structure.hasEmail) {
    dbData.email = employee.email;
  }
  
  if (employee.phone) {
    dbData.phone = employee.phone;
  }
  
  return dbData;
};

export const usePayroll = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [payrollHistory, setPayrollHistory] = useState<PayrollRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableStructure, setTableStructure] = useState<any>(null);

  // Wykryj strukturę tabeli przy pierwszym ładowaniu
  useEffect(() => {
    const init = async () => {
      const structure = await detectTableStructure();
      setTableStructure(structure);
      await loadData();
    };
    init();
  }, []);

  // Pobierz pracowników
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('Fetching employees...');
      
      const { data, error: fetchError } = await supabase
        .from('employees')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.error('Supabase error:', fetchError);
        throw fetchError;
      }

      console.log('Fetched employees:', data);
      
      const mappedEmployees = (data || []).map(emp => mapEmployeeFromDb(emp, tableStructure));
      setEmployees(mappedEmployees);
      
      return { data: mappedEmployees, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania pracowników';
      setError(errorMessage);
      console.error('Error fetching employees:', err);
      
      // Jeśli tabela nie istnieje, zwróć pustą listę
      if (errorMessage.includes('relation') && errorMessage.includes('does not exist')) {
        setEmployees([]);
        setError('Tabela pracowników nie istnieje. Wykonaj skrypt SQL z pliku payroll_schema_fix.sql');
      }
      
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [tableStructure]);

  // Pobierz historię rozliczeń z tabeli payroll_calculations
  const fetchPayrollHistory = useCallback(async () => {
    try {
      console.log('Fetching payroll history from payroll_calculations...');
      
      const { data, error: fetchError } = await supabase
        .from('payroll_calculations')
        .select(`
          *,
          employee:employees (
            first_name,
            last_name,
            email
          )
        `)
        .order('month', { ascending: false });

      if (fetchError) {
        console.error('Error fetching payroll calculations:', fetchError);
        // Jeśli tabela nie istnieje, zwróć pustą listę
        if (fetchError.message.includes('relation') && fetchError.message.includes('does not exist')) {
          setPayrollHistory([]);
          return { data: [], error: null };
        }
        throw fetchError;
      }

      console.log('Fetched payroll calculations:', data);

      // Konwertuj dane z payroll_calculations na format PayrollRecord
      const mappedRecords = (data || []).map(calc => ({
        id: calc.id,
        employeeId: calc.employee_id,
        employeeName: calc.employee 
          ? `${calc.employee.first_name} ${calc.employee.last_name}` 
          : 'Nieznany pracownik',
        month: calc.month,
        totalHours: calc.regular_hours + calc.overtime_50 + calc.overtime_100 + calc.overtime_regular,
        regularHours: calc.regular_hours,
        overtime: {
          hours50: calc.overtime_50,
          hours100: calc.overtime_100
        },
        grossAmount: calc.gross_salary,
        netAmount: calc.net_salary,
        deductions: {
          pension: calc.pension_contribution,
          disability: calc.disability_contribution,
          sickness: calc.sickness_contribution,
          health: calc.health_contribution,
          tax: calc.income_tax
        },
        createdAt: calc.created_at
      }));
      
      console.log('Mapped payroll records:', mappedRecords);
      setPayrollHistory(mappedRecords);
      return { data: mappedRecords, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd pobierania historii rozliczeń';
      console.error('Error fetching payroll history:', err);
      return { data: null, error: errorMessage };
    }
  }, []);

  const loadData = async () => {
    await Promise.all([
      fetchEmployees(),
      fetchPayrollHistory()
    ]);
  };

  // Dodaj pracownika
  const addEmployee = useCallback(async (employeeData: Omit<Employee, 'id' | 'createdAt'>) => {
    setLoading(true);
    setError(null);
    
    try {
      const dbData = mapEmployeeToDb(employeeData, tableStructure);
      
      console.log('Adding employee with data:', dbData);
      
      const { data, error: insertError } = await supabase
        .from('employees')
        .insert(dbData)
        .select()
        .single();

      if (insertError) {
        console.error('Insert error:', insertError);
        throw insertError;
      }

      console.log('Employee added:', data);
      
      const newEmployee = mapEmployeeFromDb(data, tableStructure);
      setEmployees(prev => [...prev, newEmployee]);
      
      return { data: newEmployee, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd dodawania pracownika';
      setError(errorMessage);
      console.error('Error adding employee:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [tableStructure]);

  // Aktualizuj pracownika
  const updateEmployee = useCallback(async (id: string, updates: Partial<Employee>) => {
    setLoading(true);
    setError(null);
    
    try {
      const dbData = mapEmployeeToDb(updates, tableStructure);
      
      const { data, error: updateError } = await supabase
        .from('employees')
        .update(dbData)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      const updatedEmployee = mapEmployeeFromDb(data, tableStructure);
      setEmployees(prev => prev.map(emp => 
        emp.id === id ? updatedEmployee : emp
      ));
      
      return { data: updatedEmployee, error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd aktualizacji pracownika';
      setError(errorMessage);
      console.error('Error updating employee:', err);
      return { data: null, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [tableStructure]);

  // Usuń pracownika
  const deleteEmployee = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: deleteError } = await supabase
        .from('employees')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setEmployees(prev => prev.filter(emp => emp.id !== id));
      
      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd usuwania pracownika';
      setError(errorMessage);
      console.error('Error deleting employee:', err);
      return { error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Dodaj rozliczenie (tworzy rekord w payroll_records)
  const addPayrollRecord = useCallback(async (record: Omit<PayrollRecord, 'id' | 'createdAt'>) => {
    // Ta funkcja nie jest obecnie używana, bo kalkulator zapisuje bezpośrednio do payroll_calculations
    return { data: null, error: 'Użyj kalkulatora wynagrodzeń do zapisywania rozliczeń' };
  }, []);

  // Usuń rozliczenie
  const deletePayrollRecord = useCallback(async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('payroll_calculations')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setPayrollHistory(prev => prev.filter(record => record.id !== id));
      
      return { error: null };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Błąd usuwania rozliczenia';
      console.error('Error deleting payroll record:', err);
      return { error: errorMessage };
    }
  }, []);

  const getMonthlyReport = useCallback((month: string) => {
    return payrollHistory.filter(record => record.month === month);
  }, [payrollHistory]);

  const getEmployeeHistory = useCallback((employeeId: string) => {
    return payrollHistory.filter(record => record.employeeId === employeeId);
  }, [payrollHistory]);

  return {
    employees,
    payrollHistory,
    loading,
    error,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    addPayrollRecord,
    deletePayrollRecord,
    getMonthlyReport,
    getEmployeeHistory,
    refreshData: loadData
  };
};
